import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { RepairTaskDetailDto, RepairTaskService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { openRepairTaskCloseDialog, RepairTaskCloseMode } from '@components/dialog/repair-task-close-dialog/repair-task-close-dialog.component';
import StatusEnum = RepairTaskDetailDto.StatusEnum;

@Component({
  standalone: false,
  selector: 'wr-contractor-repair-task-detail-dialog',
  templateUrl: './contractor-repair-task-detail-dialog.component.html',
  styleUrls: ['./contractor-repair-task-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorRepairTaskDetailDialogComponent implements OnInit {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  private readonly dialogRef = inject(NbDialogRef<ContractorRepairTaskDetailDialogComponent>);

  @Input() repairTaskId = '';

  readonly repairTask = signal<RepairTaskDetailDto | undefined>(undefined);
  readonly loading = signal(false);
  readonly changed = signal(false);

  readonly canClose = computed(() => {
    const task = this.repairTask();
    return !!task
      && task.permissions.canClose
      && (task.status === StatusEnum.Todo || task.status === StatusEnum.InProgress);
  });

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async close(mode: RepairTaskCloseMode): Promise<void> {
    const task = this.repairTask();
    if (!task) return;
    const result = await openRepairTaskCloseDialog(this.dialogService, mode, task.title);
    if (!result) return;

    this.loading.set(true);
    try {
      const updated = mode === 'finish'
        ? await firstValueFrom(this.repairTaskService.finish(task.id, result))
        : await firstValueFrom(this.repairTaskService.decline(task.id, { comment: result.comment }));
      this.repairTask.set(updated);
      this.changed.set(true);
      this.toastrService.success(
        mode === 'finish' ? 'Reparaturauftrag abgeschlossen.' : 'Reparaturauftrag storniert.',
        'Gespeichert',
      );
    } catch {
      await this.reloadAfterFailedClose();
    } finally {
      this.loading.set(false);
    }
  }

  dismiss(): void {
    this.dialogRef.close(this.changed());
  }

  private async reloadAfterFailedClose(): Promise<void> {
    this.changed.set(true);
    try {
      await this.load();
    } catch {
      this.repairTask.set(undefined);
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    try {
      this.repairTask.set(await firstValueFrom(this.repairTaskService.findById(this.repairTaskId)));
    } finally {
      this.loading.set(false);
    }
  }
}
